/**
 * ArtPart illustration system – hand-drawn architectural / anatomical studies.
 * Every illustration is a reusable SVG component: transparent, ink black with
 * graphite greys, construction lines, measure ticks, partially open contours and
 * at most two small pink markers. Decorative by default (aria-hidden).
 */
const INK = "#0B0B0C";
const GRAPHITE = "#3C3E43";
const GREY = "#8C9099";
const PINK = "#FF2A85";

const Wrap = ({ children, viewBox = "0 0 120 120", className = "", title }) => (
  <svg
    viewBox={viewBox}
    className={className}
    fill="none"
    role={title ? "img" : "presentation"}
    aria-hidden={title ? undefined : "true"}
    aria-label={title}
    xmlns="http://www.w3.org/2000/svg"
  >
    {children}
  </svg>
);

/** thin construction grid used behind several studies */
const Grid = ({ x = 8, y = 8, w = 104, h = 104, step = 16, opacity = 0.35 }) => {
  const lines = [];
  for (let gx = x; gx <= x + w; gx += step)
    lines.push(<line key={`v${gx}`} x1={gx} y1={y} x2={gx} y2={y + h} stroke={GREY} strokeWidth="0.3" />);
  for (let gy = y; gy <= y + h; gy += step)
    lines.push(<line key={`h${gy}`} x1={x} y1={gy} x2={x + w} y2={gy} stroke={GREY} strokeWidth="0.3" />);
  return <g opacity={opacity}>{lines}</g>;
};

const Ticks = ({ x = 10, y = 112, count = 6, step = 16 }) => (
  <g opacity="0.7">
    {Array.from({ length: count }).map((_, i) => (
      <line
        key={i}
        x1={x + i * step}
        y1={y}
        x2={x + i * step}
        y2={y + (i % 2 ? 3 : 5)}
        stroke={GRAPHITE}
        strokeWidth="0.5"
      />
    ))}
  </g>
);

/** 1 – Ausgewählte Arbeiten: stacked photo prints */
export const IlluPrints = ({ className }) => (
  <Wrap className={className}>
    <Grid opacity={0.18} />
    <g stroke={INK} strokeLinecap="square">
      <rect x="16" y="24" width="52" height="62" strokeWidth="1" fill="#fff" fillOpacity="0.55" transform="rotate(-6 42 55)" />
      <rect x="30" y="18" width="54" height="64" strokeWidth="1.3" fill="#fff" fillOpacity="0.8" transform="rotate(3 57 50)" />
      <rect x="40" y="30" width="52" height="62" strokeWidth="1.6" fill="#fff" fillOpacity="0.9" />
      <rect x="45" y="35" width="42" height="40" strokeWidth="0.7" fill={GRAPHITE} fillOpacity="0.85" />
      <path d="M45 66 L58 52 L66 60 L74 49 L87 63" strokeWidth="0.8" stroke="#fff" opacity="0.55" />
      <path d="M40 92 L92 92" strokeWidth="0.6" opacity="0.6" />
    </g>
    <line x1="92" y1="30" x2="106" y2="30" stroke={PINK} strokeWidth="1.6" />
    <circle cx="45" cy="35" r="1.4" fill={PINK} />
    <Ticks />
  </Wrap>
);

/** 2 – Verfügbare Motive: monumental portal */
export const IlluPortal = ({ className }) => (
  <Wrap className={className}>
    <Grid opacity={0.2} />
    <g stroke={INK} strokeLinecap="square">
      <path d="M18 100 L18 26 L100 20 L100 100" strokeWidth="1.8" />
      <path d="M40 100 L40 46 L78 42 L78 100" strokeWidth="1.4" />
      <path d="M18 26 L40 46 M100 20 L78 42" strokeWidth="0.7" opacity="0.8" />
      <g strokeWidth="0.45" opacity="0.75">
        {Array.from({ length: 7 }).map((_, i) => (
          <line key={i} x1={20 + i * 2.6} y1={30 + i} x2={20 + i * 2.6} y2={99} />
        ))}
        {Array.from({ length: 7 }).map((_, i) => (
          <line key={`r${i}`} x1={98 - i * 2.6} y1={24 + i} x2={98 - i * 2.6} y2={99} />
        ))}
      </g>
      <path d="M10 100 L112 100" strokeWidth="1" />
      <path d="M30 108 L64 104" strokeWidth="0.5" stroke={GREY} />
    </g>
    <line x1="78" y1="14" x2="98" y2="14" stroke={PINK} strokeWidth="1.6" />
    <Ticks y={104} count={5} step={20} />
  </Wrap>
);

/** 3 – Dystopisches Projekt: fragmented brutalist tower */
export const IlluTower = ({ className }) => (
  <Wrap className={className}>
    <g stroke={INK} strokeLinecap="square">
      <path d="M44 104 L44 30 L74 22 L74 104" strokeWidth="1.6" fill={GRAPHITE} fillOpacity="0.12" />
      <path d="M74 40 L96 34 L96 58 L74 62" strokeWidth="1.2" fill={INK} fillOpacity="0.72" />
      <path d="M22 52 L44 46 L44 70 L22 74 Z" strokeWidth="1.2" fill={INK} fillOpacity="0.5" />
      <path d="M50 78 L68 74 L68 92 L50 96 Z" strokeWidth="1" fill={GRAPHITE} fillOpacity="0.35" />
      <g strokeWidth="0.4" opacity="0.7">
        {Array.from({ length: 9 }).map((_, i) => (
          <line key={i} x1={46} y1={34 + i * 7} x2={72} y2={32 + i * 7} />
        ))}
      </g>
      <path d="M14 104 L110 104" strokeWidth="1" />
      <path d="M96 34 L106 26 M22 52 L12 44" strokeWidth="0.5" stroke={GREY} />
      <path d="M74 22 L86 14" strokeWidth="0.5" stroke={GREY} />
    </g>
    <line x1="86" y1="20" x2="104" y2="20" stroke={PINK} strokeWidth="1.6" />
    <circle cx="50" cy="96" r="1.3" fill={PINK} />
    <Ticks y={108} count={5} step={22} />
  </Wrap>
);

/** 4 – Custom-Projekt: anatomical head in a construction grid */
export const IlluHead = ({ className }) => (
  <Wrap className={className}>
    <Grid opacity={0.3} />
    <g stroke={INK} strokeLinecap="round">
      <path
        d="M74 22 C58 18 42 26 38 42 C35 54 40 60 38 66 C36 72 30 74 32 80 C34 86 44 86 46 92 L48 104"
        strokeWidth="1.5"
      />
      <path d="M38 42 C36 34 44 24 56 22" strokeWidth="0.6" opacity="0.7" />
      <path d="M74 22 C86 30 88 52 82 68 C78 80 70 88 64 104" strokeWidth="1.5" />
      <path d="M46 92 L64 96" strokeWidth="0.8" />
      <path d="M44 52 C50 50 54 52 56 56" strokeWidth="0.7" />
      <path d="M40 62 L50 64" strokeWidth="0.6" />
      <path d="M60 30 C68 34 72 44 70 54 C68 64 62 70 58 74" strokeWidth="0.5" opacity="0.65" />
      <g strokeWidth="0.4" opacity="0.6">
        {Array.from({ length: 6 }).map((_, i) => (
          <line key={i} x1={58 + i * 3} y1={34} x2={58 + i * 3} y2={78} />
        ))}
      </g>
      <path d="M8 42 L112 38" strokeWidth="0.35" stroke={GREY} />
      <path d="M8 80 L112 78" strokeWidth="0.35" stroke={GREY} />
    </g>
    <line x1="84" y1="46" x2="104" y2="46" stroke={PINK} strokeWidth="1.6" />
    <circle cx="38" cy="66" r="1.3" fill={PINK} />
    <Ticks y={110} count={6} step={17} />
  </Wrap>
);

/** 5 – Anfrage: document and pen in a grid */
export const IlluDocument = ({ className }) => (
  <Wrap className={className}>
    <Grid opacity={0.2} />
    <g stroke={INK} strokeLinecap="square">
      <path d="M30 20 L74 20 L88 34 L88 100 L30 100 Z" strokeWidth="1.5" fill="#fff" fillOpacity="0.7" />
      <path d="M74 20 L74 34 L88 34" strokeWidth="0.9" />
      <g strokeWidth="0.6" opacity="0.8">
        {[46, 54, 62, 70, 78].map((y) => (
          <line key={y} x1={38} y1={y} x2={y === 78 ? 66 : 80} y2={y} />
        ))}
      </g>
      <path d="M96 26 L104 34 L66 72 L58 78 L62 68 Z" strokeWidth="1.1" fill={GRAPHITE} fillOpacity="0.25" />
      <path d="M96 26 L104 34" strokeWidth="0.6" />
      <path d="M18 100 L104 100" strokeWidth="0.8" />
    </g>
    <line x1="38" y1="88" x2="56" y2="88" stroke={PINK} strokeWidth="1.6" />
    <Ticks y={104} count={5} step={20} />
  </Wrap>
);

/** 6 – Rückmeldung: technically drawn speech volume over a head profile */
export const IlluResponse = ({ className }) => (
  <Wrap className={className}>
    <Grid opacity={0.18} />
    <g stroke={INK} strokeLinecap="square">
      <path d="M22 84 C18 66 26 46 44 40 C58 35 66 44 66 56 C66 68 58 74 50 78 L46 100" strokeWidth="1.3" />
      <path d="M46 100 L28 100" strokeWidth="0.7" />
      <path d="M40 26 L106 20 L106 62 L70 66 L58 78 L62 64 L40 66 Z" strokeWidth="1.5" fill="#fff" fillOpacity="0.72" />
      <g strokeWidth="0.6" opacity="0.85">
        <line x1="50" y1="38" x2="96" y2="35" />
        <line x1="50" y1="46" x2="96" y2="43" />
        <line x1="50" y1="54" x2="78" y2="52" />
      </g>
      <path d="M10 84 L112 82" strokeWidth="0.35" stroke={GREY} />
    </g>
    <line x1="86" y1="72" x2="104" y2="72" stroke={PINK} strokeWidth="1.6" />
    <circle cx="50" cy="54" r="1.2" fill={PINK} />
    <Ticks y={106} count={6} step={17} />
  </Wrap>
);

/** 7 – Entwurf: body / arm mapping */
export const IlluMapping = ({ className }) => (
  <Wrap className={className}>
    <Grid opacity={0.28} />
    <g stroke={INK} strokeLinecap="round">
      <path d="M60 18 C54 18 52 24 54 30 C50 34 42 36 38 44 C34 54 34 66 32 76" strokeWidth="1.3" />
      <path d="M60 18 C66 18 68 24 66 30 C70 34 78 36 82 44 C86 54 86 66 88 76" strokeWidth="1.3" />
      <path d="M46 42 L46 78 M74 42 L74 78" strokeWidth="0.6" opacity="0.7" />
      <path d="M46 78 C50 92 50 98 48 106 M74 78 C70 92 70 98 72 106" strokeWidth="1.1" />
      <path d="M52 48 C58 52 62 52 68 48" strokeWidth="0.6" />
      <path d="M50 60 C58 64 62 64 70 60" strokeWidth="0.6" />
      <path d="M32 76 L20 72 M88 76 L100 72" strokeWidth="0.5" stroke={GREY} />
      <g strokeWidth="0.4" stroke={GREY} opacity="0.8">
        <line x1="14" y1="44" x2="106" y2="42" />
        <line x1="14" y1="78" x2="106" y2="76" />
        <line x1="14" y1="44" x2="14" y2="78" />
      </g>
    </g>
    <line x1="90" y1="52" x2="106" y2="52" stroke={PINK} strokeWidth="1.6" />
    <circle cx="60" cy="60" r="1.3" fill={PINK} />
    <Ticks y={110} count={6} step={17} />
  </Wrap>
);

/** 8 – Termin: calendar as architectural diagram */
export const IlluCalendar = ({ className }) => (
  <Wrap className={className}>
    <g stroke={INK} strokeLinecap="square">
      <path d="M20 32 L100 26 L100 98 L20 98 Z" strokeWidth="1.6" fill="#fff" fillOpacity="0.65" />
      <path d="M20 48 L100 43" strokeWidth="1" />
      <path d="M34 20 L34 40 M86 18 L86 38" strokeWidth="1.2" />
      <g strokeWidth="0.45" opacity="0.8">
        {[40, 60, 80].map((x) => (
          <line key={x} x1={x} y1={46} x2={x} y2={98} />
        ))}
        {[64, 80].map((y) => (
          <line key={y} x1={20} y1={y} x2={100} y2={y - 1} />
        ))}
      </g>
      <rect x="62" y="66" width="17" height="13" fill={INK} fillOpacity="0.8" strokeWidth="0.5" />
      <path d="M12 98 L108 98" strokeWidth="0.8" />
      <path d="M100 26 L110 18" strokeWidth="0.5" stroke={GREY} />
    </g>
    <line x1="24" y1="20" x2="42" y2="20" stroke={PINK} strokeWidth="1.6" />
    <circle cx="70" cy="72" r="1.4" fill={PINK} />
    <Ticks y={102} count={5} step={20} />
  </Wrap>
);

/** 9 – Archiv: black monolith */
export const IlluMonolith = ({ className }) => (
  <Wrap className={className}>
    <g stroke={INK} strokeLinecap="square">
      <path d="M34 96 L34 26 L78 18 L92 28 L92 88 L34 96 Z" strokeWidth="1.4" fill={INK} fillOpacity="0.92" />
      <path d="M78 18 L78 30 L92 28" strokeWidth="0.7" stroke={GREY} />
      <path d="M44 40 L60 38" strokeWidth="0.6" stroke="#fff" opacity="0.4" />
      <path d="M20 96 L104 92" strokeWidth="0.8" />
      <path d="M34 26 L22 20" strokeWidth="0.5" stroke={GREY} />
    </g>
    <circle cx="82" cy="38" r="1.6" fill={PINK} />
    <Ticks y={100} count={4} step={24} />
  </Wrap>
);

/** 10 – Bestellung: document in a broken architectural frame */
export const IlluOrder = ({ className }) => (
  <Wrap className={className}>
    <g stroke={INK} strokeLinecap="square">
      <path d="M16 24 L104 18 L104 62" strokeWidth="1.6" />
      <path d="M104 78 L104 102 L16 102 L16 60" strokeWidth="1.6" />
      <path d="M16 40 L16 30" strokeWidth="1.6" />
      <path d="M40 34 L82 30 L82 90 L40 90 Z" strokeWidth="1.2" fill="#fff" fillOpacity="0.75" />
      <g strokeWidth="0.6" opacity="0.85">
        {[46, 54, 62, 70].map((y) => (
          <line key={y} x1={48} y1={y} x2={y === 70 ? 66 : 74} y2={y - 1} />
        ))}
      </g>
      <path d="M104 62 L112 70 L104 78" strokeWidth="0.6" stroke={GREY} />
      <path d="M16 40 L8 50 L16 60" strokeWidth="0.6" stroke={GREY} />
    </g>
    <line x1="48" y1="80" x2="66" y2="80" stroke={PINK} strokeWidth="1.6" />
    <Ticks y={106} count={5} step={20} />
  </Wrap>
);

/** 11 – Standort: plan marker in a coordinate grid */
export const IlluLocation = ({ className }) => (
  <Wrap className={className}>
    <Grid opacity={0.35} />
    <g stroke={INK} strokeLinecap="square">
      <path d="M60 96 C60 96 40 68 40 52 C40 39 49 30 60 30 C71 30 80 39 80 52 C80 68 60 96 60 96 Z" strokeWidth="1.5" fill={INK} fillOpacity="0.08" />
      <circle cx="60" cy="51" r="7" strokeWidth="1" />
      <path d="M14 96 L106 94" strokeWidth="0.7" />
      <path d="M60 12 L60 30 M22 51 L40 51 M80 51 L98 51" strokeWidth="0.45" stroke={GREY} />
    </g>
    <circle cx="60" cy="51" r="2" fill={PINK} />
    <Ticks y={100} count={6} step={17} />
  </Wrap>
);

/** 12 – Pflege: skin surface with technical markings */
export const IlluCare = ({ className }) => (
  <Wrap className={className}>
    <g stroke={INK} strokeLinecap="round">
      <path d="M28 96 C24 78 30 58 42 46 C52 36 66 32 78 36 C90 40 96 52 94 64 C92 78 82 88 70 96" strokeWidth="1.4" />
      <path d="M40 88 C46 74 56 66 70 62" strokeWidth="0.6" opacity="0.7" />
      <path d="M50 92 C56 80 66 74 80 72" strokeWidth="0.6" opacity="0.7" />
      <g strokeWidth="0.4" stroke={GREY}>
        <line x1="20" y1="52" x2="104" y2="48" />
        <line x1="20" y1="76" x2="104" y2="72" />
      </g>
      <circle cx="66" cy="58" r="9" strokeWidth="0.7" strokeDasharray="2 2" />
    </g>
    <line x1="86" y1="86" x2="102" y2="86" stroke={PINK} strokeWidth="1.6" />
    <Ticks y={104} count={5} step={20} />
  </Wrap>
);

/** 13 – Bewertung: geometric star */
export const IlluStar = ({ className }) => (
  <Wrap className={className}>
    <Grid opacity={0.2} />
    <g stroke={INK} strokeLinecap="square">
      <path d="M60 22 L70 50 L100 52 L76 70 L84 98 L60 82 L36 98 L44 70 L20 52 L50 50 Z" strokeWidth="1.4" fill={INK} fillOpacity="0.1" />
      <path d="M60 22 L60 82 M20 52 L100 52" strokeWidth="0.4" stroke={GREY} />
    </g>
    <circle cx="60" cy="52" r="1.6" fill={PINK} />
    <Ticks y={104} count={5} step={20} />
  </Wrap>
);

/** 14 – E-Mail: constructed envelope */
export const IlluMail = ({ className }) => (
  <Wrap className={className}>
    <g stroke={INK} strokeLinecap="square">
      <path d="M18 34 L102 28 L102 92 L18 92 Z" strokeWidth="1.5" fill="#fff" fillOpacity="0.65" />
      <path d="M18 34 L60 64 L102 28" strokeWidth="1" />
      <path d="M18 92 L52 60 M102 92 L68 60" strokeWidth="0.5" opacity="0.75" />
      <path d="M10 92 L110 90" strokeWidth="0.7" />
    </g>
    <line x1="80" y1="22" x2="100" y2="22" stroke={PINK} strokeWidth="1.6" />
    <Ticks y={98} count={5} step={20} />
  </Wrap>
);

/** Registry so every illustration can be assigned per section from the backend. */
export const ILLUSTRATIONS = {
  prints: IlluPrints,
  portal: IlluPortal,
  tower: IlluTower,
  head: IlluHead,
  document: IlluDocument,
  response: IlluResponse,
  mapping: IlluMapping,
  calendar: IlluCalendar,
  monolith: IlluMonolith,
  order: IlluOrder,
  location: IlluLocation,
  care: IlluCare,
  star: IlluStar,
  mail: IlluMail,
};

export const ILLUSTRATION_OPTIONS = [
  ["prints", "Fotoabzüge (Arbeiten)"],
  ["portal", "Architekturportal (Motive)"],
  ["tower", "Fragmentierter Turm (Dystopisch)"],
  ["head", "Anatomischer Kopf (Custom)"],
  ["document", "Dokument & Stift (Anfrage)"],
  ["response", "Rückmeldung (Gespräch)"],
  ["mapping", "Körper-Mapping (Entwurf)"],
  ["calendar", "Kalender (Termin)"],
  ["monolith", "Monolith (Archiv)"],
  ["order", "Bestellung (Dokument im Rahmen)"],
  ["location", "Standort (Planmarker)"],
  ["care", "Pflege (Hautfläche)"],
  ["star", "Bewertung (Stern)"],
  ["mail", "E-Mail (Kuvert)"],
];

/** Renders an illustration by registry name with a safe fallback. */
export const Illu = ({ name, fallback = "portal", className = "w-full h-auto" }) => {
  const Component = ILLUSTRATIONS[name] || ILLUSTRATIONS[fallback];
  return <Component className={className} />;
};
